import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VerifiedIcon from '@mui/icons-material/Verified';
import { decryptGpgPayload } from '../api/mailbox';
import GpgPassphraseDialog from './GpgPassphraseDialog';
import { formatApiError } from '../utils/emailAddress';
import { looksLikePgpMessage, looksLikePgpPayload, looksLikePgpSigned, pgpSourceText } from '../utils/pgp';

interface MessagePgpActionsProps {
  bodyText?: string | null;
  bodyHtml?: string | null;
}

export default function MessagePgpActions({ bodyText, bodyHtml }: MessagePgpActionsProps) {
  const source = useMemo(() => pgpSourceText(bodyText, bodyHtml), [bodyText, bodyHtml]);
  const encrypted = looksLikePgpMessage(source);
  const signed = looksLikePgpSigned(source);
  const actionable = looksLikePgpPayload(source);

  const [promptOpen, setPromptOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  if (!actionable) return null;

  const runDecrypt = async (passphrase: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await decryptGpgPayload(source, passphrase);
      setPlaintext(result.plaintext || '');
      setVerified(Boolean(result.verified));
      setPromptOpen(false);
    } catch (err: unknown) {
      setError(formatApiError((err as { response?: { data?: unknown } })?.response?.data, 'Decryption failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {encrypted && (
          <Alert severity="warning" sx={{ flexGrow: 1 }}>
            This message looks PGP-encrypted.
          </Alert>
        )}
        {signed && !encrypted && (
          <Alert severity="info" sx={{ flexGrow: 1 }}>
            This message looks PGP-signed.
          </Alert>
        )}
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : encrypted ? <LockOpenIcon /> : <VerifiedIcon />}
          disabled={busy}
          onClick={() => {
            setError(null);
            setPromptOpen(true);
          }}
        >
          {encrypted ? 'Decrypt' : 'Verify / reveal'}
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {plaintext !== null && (
        <Box sx={{ mt: 1 }}>
          {verified && (
            <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.5 }}>
              Signature/decryption OK
            </Typography>
          )}
          <Box
            component="pre"
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              m: 0,
            }}
          >
            {plaintext || '(empty)'}
          </Box>
        </Box>
      )}

      <GpgPassphraseDialog
        open={promptOpen}
        title={encrypted ? 'Decrypt message' : 'Open signed message'}
        description={
          encrypted
            ? 'Enter the passphrase for your secret key to decrypt this message.'
            : 'Enter your secret-key passphrase if needed to verify this signed message.'
        }
        confirmLabel={encrypted ? 'Decrypt' : 'Continue'}
        busy={busy}
        error={error}
        onCancel={() => setPromptOpen(false)}
        onConfirm={runDecrypt}
      />
    </Box>
  );
}
